import { useEffect, useMemo, useState } from 'react';
import type { TrackerSet } from '@/domain/content.types';
import { localIsoDate } from '@/domain/schedule';
import { pickName } from '@/i18n/itemName';
import { useT } from '@/i18n/useT';
import { layoutCard, type CardGroup } from '@/share/cardLayout';
import { renderCard } from '@/share/renderCard';
import { renderMapCard } from '@/share/renderMapCard';
import { useProgress } from '@/state/progressStore';
import { useSettings, viewFor } from '@/state/settingsStore';
import { useToast } from '@/state/toastStore';

interface ShareSheetProps {
  set: TrackerSet;
  onClose: () => void;
}

type Status = 'rendering' | 'ready' | 'failed';

/**
 * Renders the progress picture and offers it three ways, each shown only
 * where the browser can actually do it: the native share sheet (phones —
 * this is how it reaches Discord), the clipboard (desktop, paste into chat),
 * and a plain download. The preview itself is a real <img>, so on iOS a
 * long-press to save works even if all three buttons are missing.
 */
export function ShareSheet({ set, onClose }: ShareSheetProps) {
  const { t, lang } = useT();
  const [onlyDone, setOnlyDone] = useState(false);
  // Start on whichever view the user was looking at; sets without a map only
  // ever render the list picture.
  const currentView = viewFor(
    useSettings((s) => s.views),
    set.id,
    set.map !== undefined,
  );
  const [variant, setVariant] = useState<'list' | 'map'>(
    set.map && currentView === 'map' ? 'map' : 'list',
  );
  const [status, setStatus] = useState<Status>('rendering');
  const [blob, setBlob] = useState<Blob | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const showToast = useToast((s) => s.show);

  const setProgress = useProgress((s) => s.snapshot.sets[set.id]);
  const profiles = useProgress((s) => s.profiles);
  const activeProfileId = useProgress((s) => s.activeProfileId);
  const profileName = profiles.find((p) => p.id === activeProfileId)?.name ?? '';

  const today = localIsoDate();
  const filename = `meowtracker-${set.slug}-${today}.png`;
  const file = useMemo(() => (blob ? new File([blob], filename, { type: 'image/png' }) : null), [blob, filename]);

  const canShare =
    file !== null && typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] });
  const canCopy =
    typeof ClipboardItem !== 'undefined' && typeof navigator.clipboard?.write === 'function';

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setStatus('rendering');

    const date = new Intl.DateTimeFormat(lang, { dateStyle: 'long' }).format(new Date());
    const subtitle = [profileName, date].filter(Boolean).join(' · ');
    const footer = `${t('app.name')} · ${t('app.tagline')}`;

    let render: Promise<Blob>;
    if (variant === 'map' && set.map) {
      const doneOf = (g: TrackerSet['groups'][number]): number =>
        g.items.filter((item) => setProgress?.[item.id]?.done === true).length;
      const pins = set.groups
        .filter((g): g is TrackerSet['groups'][number] & { pin: { u: number; v: number } } => g.pin !== undefined)
        .map((g) => ({ title: g.title[lang], u: g.pin.u, v: g.pin.v, done: doneOf(g), total: g.items.length }));
      const done = set.groups.reduce((n, g) => n + doneOf(g), 0);
      const total = set.groups.reduce((n, g) => n + g.items.length, 0);
      render = renderMapCard({
        title: set.title[lang],
        subtitle,
        footer,
        attribution: set.map.attribution[lang],
        image: set.map.image,
        map: { width: set.map.width, height: set.map.height },
        pins,
        done,
        total,
        placesDone: pins.filter((p) => p.total > 0 && p.done === p.total).length,
      });
    } else {
      const groups: CardGroup[] = set.groups.map((g) => ({
        id: g.id,
        title: g.title[lang],
        items: g.items.map((item) => ({
          id: item.id,
          name: pickName(item, lang).value,
          done: setProgress?.[item.id]?.done === true,
        })),
      }));
      const layout = layoutCard({
        title: set.title[lang],
        subtitle,
        footer,
        emptyText: t('share.nothingYet'),
        groups,
        onlyDone,
      });
      render = renderCard(layout);
    }

    render
      .then((png) => {
        if (cancelled) return;
        setBlob(png);
        setUrl(URL.createObjectURL(png));
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [set, setProgress, lang, onlyDone, variant, profileName, t]);

  // Each object URL is released when the next one replaces it, and on unmount.
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url],
  );

  const share = async (): Promise<void> => {
    if (!file) return;
    try {
      await navigator.share({ files: [file], title: set.title[lang] });
    } catch {
      // The user dismissed the sheet; nothing to report.
    }
  };

  const copy = async (): Promise<void> => {
    if (!blob) return;
    try {
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      showToast({ message: t('share.copied') });
    } catch {
      showToast({ message: t('share.copyFailed') });
    }
  };

  const chip = (active: boolean): string =>
    `rounded-xs border px-2 py-1 text-[12px] leading-4 transition-colors ${
      active
        ? 'border-pip-dim bg-pip-faint/30 text-pip'
        : 'border-line bg-surface text-text-dim hover:border-pip-dim/60'
    }`;
  const action =
    'rounded-xs border border-line bg-surface-2 px-2.5 py-1.5 text-[12px] leading-4 text-text hover:border-pip-dim hover:text-pip';

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-3"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('share.title')}
        className="flex max-h-full w-full max-w-3xl flex-col rounded-sm border border-line bg-surface"
      >
        <header className="flex items-center gap-2 border-b border-line px-3 py-2">
          <h2 className="text-[13px] leading-5 font-semibold text-pip">{t('share.title')}</h2>
          {set.map ? (
            <div className="ml-auto flex gap-1" role="group" aria-label={t('view.map')}>
              <button type="button" onClick={() => setVariant('list')} aria-pressed={variant === 'list'} className={chip(variant === 'list')}>
                {t('share.variantList')}
              </button>
              <button type="button" onClick={() => setVariant('map')} aria-pressed={variant === 'map'} className={chip(variant === 'map')}>
                {t('share.variantMap')}
              </button>
            </div>
          ) : null}
          <div
            className={(set.map ? '' : 'ml-auto ') + 'flex gap-1' + (variant === 'map' ? ' invisible' : '')}
            role="group"
            aria-label={t('share.title')}
          >
            <button type="button" onClick={() => setOnlyDone(false)} aria-pressed={!onlyDone} className={chip(!onlyDone)}>
              {t('share.modeAll')}
            </button>
            <button type="button" onClick={() => setOnlyDone(true)} aria-pressed={onlyDone} className={chip(onlyDone)}>
              {t('share.modeDone')}
            </button>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="rounded-xs border border-line px-1.5 py-0.5 text-[12px] leading-4 text-text-faint hover:text-pip"
          >
            ✕
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-auto bg-bg p-3">
          {status === 'failed' ? (
            <p className="py-10 text-center text-[12px] leading-4 text-danger">{t('share.failed')}</p>
          ) : url ? (
            <img
              src={url}
              alt={t('share.imageAlt')}
              className={`mx-auto w-full max-w-[720px] rounded-xs border border-line ${
                status === 'rendering' ? 'opacity-50' : ''
              }`}
            />
          ) : (
            <p className="py-10 text-center text-[12px] leading-4 text-text-faint">
              {t('share.rendering')}
            </p>
          )}
        </div>

        <footer className="flex flex-wrap items-center gap-2 border-t border-line px-3 py-2">
          {canShare ? (
            <button type="button" onClick={() => void share()} disabled={status !== 'ready'} className={action}>
              {t('share.share')}
            </button>
          ) : null}
          {canCopy ? (
            <button type="button" onClick={() => void copy()} disabled={status !== 'ready'} className={action}>
              {t('share.copy')}
            </button>
          ) : null}
          {url ? (
            <a href={url} download={filename} className={action}>
              {t('share.download')}
            </a>
          ) : null}
          {!canShare ? (
            <span className="text-[11px] leading-4 text-text-faint">{t('share.hint')}</span>
          ) : null}
        </footer>
      </div>
    </div>
  );
}
